import React, {useCallback, useEffect, useRef} from 'react';
import {Box} from '@mui/material';
import {disableSuggestChanges, suggestChangesKey} from '@handlewithcare/prosemirror-suggest-changes';
import {Editor} from '../../../components/prosemirror-editor';
import {useSession} from '../../../firebase/UserProvider';

const PLACEHOLDER_TEXT = 'Start writing...';

/**
 * Clear the legacy bootstrap placeholder when it is the only content.
 * New docs get an empty template server-side; this covers already-persisted docs.
 */
function clearLegacyPlaceholder(view) {
    if (!view || view.isDestroyed) return;
    const {state, dispatch} = view;
    const {doc, schema, tr} = state;
    if (doc.childCount !== 1) return;
    const only = doc.firstChild;
    if (!only || only.type.name !== 'paragraph') return;
    if (only.textContent !== PLACEHOLDER_TEXT) return;
    const empty = schema.nodes.paragraph.create();
    dispatch(tr.replaceWith(0, doc.content.size, empty).setMeta('addToHistory', false));
}

/**
 * Stripped ProseMirror editor for Collected Materials and IIRs:
 * rich text, images, tables, classification marks — no AI/NER/EL/meta-marks.
 */
export default function BasicRichTextEditor({
    documentId,
    documentHeader = null,
    showDocumentHeader = true,
    showToolbar = true,
    readOnly = false,
    onReady = null,
    seedPmJson = null,
}) {
    const {user} = useSession();
    const clearedRef = useRef(false);
    const editorUser = user
        ? {
            uid: user.uid || user.id || user.userId,
            email: user.email,
            displayName: user.displayName || user.name || user.email,
        }
        : null;

    useEffect(() => {
        clearedRef.current = false;
    }, [documentId]);

    const handleReady = useCallback((view, provider) => {
        if (!readOnly && view && !view.isDestroyed) {
            const inSuggestionMode = suggestChangesKey.getState(view.state)?.enabled ?? false;
            if (inSuggestionMode) {
                disableSuggestChanges(view.state, view.dispatch);
            }
        }
        if (!readOnly && !clearedRef.current) {
            const runClear = () => {
                if (clearedRef.current) return;
                clearedRef.current = true;
                clearLegacyPlaceholder(view);
            };
            if (provider?.synced) {
                runClear();
            } else if (provider?.on) {
                const onSynced = (isSynced) => {
                    if (!isSynced) return;
                    provider.off?.('synced', onSynced);
                    runClear();
                };
                provider.on('synced', onSynced);
            } else {
                runClear();
            }
        }
        if (onReady) onReady(view, provider);
    }, [onReady, readOnly]);

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Editor
                documentId={documentId}
                user={editorUser}
                showToolbar={showToolbar && !readOnly}
                showDocumentHeader={showDocumentHeader}
                documentHeader={documentHeader}
                editorMode="basic"
                readOnly={readOnly}
                marginRail={null}
                snippetDrop={null}
                seedPmJson={seedPmJson}
                onReady={handleReady}
            />
        </Box>
    );
}
